<script>
	import { onMount } from 'svelte';
	import { getApi } from '../../service/api';
	import { menu, menuSub, menuSubSub } from '../../stores';
	import Pagination from '../../components/Pagination.svelte';
	import TableToolbar from '../../components/TableToolbar.svelte';

	const PAGE_SIZES = [50, 100, 200];
	const EVENT_TYPE_COLOR = {
		connect: '#28a745',
		disconnect: '#dc3545',
		publish: '#007bff',
		will_sent: '#ffc107',
	};
	const EVENT_TYPE_LABEL = {
		connect: '연결',
		disconnect: '연결 끊김',
		publish: '메시지 발행',
		will_sent: '유언 발송',
	};

	// 상태
	let list = [];
	let total = 0;
	let totalPages = 1;
	let currentPage = 1;
	let pageSize = 50;
	let loading = false;
	let stats = [];
	let mqttIds = [];

	// 필터
	let eventType = '';
	let mqttId = '';
	let clientId = '';
	let topic = '';
	let startTime = '';
	let endTime = '';

	// 상세 모달
	let selectedItem = null;
	let showDetailModal = false;

	async function fetchLogs() {
		loading = true;
		try {
			let url = `/mqtt/logs/${pageSize}/${currentPage}`;
			const params = [];
			if (eventType) params.push(`eventType=${encodeURIComponent(eventType)}`);
			if (mqttId) params.push(`mqttId=${encodeURIComponent(mqttId)}`);
			if (clientId) params.push(`clientId=${encodeURIComponent(clientId)}`);
			if (topic) params.push(`topic=${encodeURIComponent(topic)}`);
			if (startTime) params.push(`startTime=${encodeURIComponent(startTime)}`);
			if (endTime) params.push(`endTime=${encodeURIComponent(endTime)}`);
			if (params.length > 0) url += '?' + params.join('&');

			const res = await getApi(url);
			if (res && res.resultCode === 1000) {
				list = res.data?.list || [];
				total = res.data?.total || 0;
				totalPages = res.data?.totalPage || 1;
				stats = res.data?.stats || [];
				mqttIds = res.data?.mqttIds || [];
			}
		} catch (e) {
			console.error('MQTT 로그 로드 실패:', e);
		}
		loading = false;
	}

	function handleSearch() {
		currentPage = 1;
		fetchLogs();
	}

	function handleReset() {
		eventType = '';
		mqttId = '';
		clientId = '';
		topic = '';
		startTime = '';
		endTime = '';
		currentPage = 1;
		fetchLogs();
	}

	function handlePageChange(page) {
		currentPage = page;
		fetchLogs();
	}

	function handlePageSizeChange(event) {
		pageSize = parseInt(event.target.value);
		currentPage = 1;
		fetchLogs();
	}

	function handleKeyPress(event) {
		if (event.key === 'Enter') {
			handleSearch();
		}
	}

	function toggleDetailModal(item) {
		if (showDetailModal) {
			showDetailModal = false;
			selectedItem = null;
		} else {
			selectedItem = item;
			showDetailModal = true;
		}
	}

	function formatDateTime(dateStr) {
		if (!dateStr) return '-';
		const d = new Date(dateStr);
		return d.toLocaleString('ko-KR', {
			year: 'numeric', month: '2-digit', day: '2-digit',
			hour: '2-digit', minute: '2-digit', second: '2-digit'
		});
	}

	function truncate(str, maxLen = 80) {
		if (!str) return '-';
		return str.length > maxLen ? str.substring(0, maxLen) + '...' : str;
	}

	function getStatCount(type) {
		const found = stats.find(s => s.eventType === type);
		return found ? found.count : 0;
	}

	function formatPayload(str) {
		if (!str) return '-';
		try {
			const obj = JSON.parse(str);
			const json = JSON.stringify(obj, null, 2);
			return json
				.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
				.replace(/"([^"]+)"(?=\s*:)/g, '<span class="json-key">"$1"</span>')
				.replace(/:\s*"([^"]*)"/g, ': <span class="json-string">"$1"</span>')
				.replace(/:\s*(\d+\.?\d*)/g, ': <span class="json-number">$1</span>')
				.replace(/:\s*(true|false)/g, ': <span class="json-bool">$1</span>')
				.replace(/:\s*(null)/g, ': <span class="json-null">$1</span>');
		} catch {
			return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
		}
	}

	onMount(() => {
		menu.set('모니터링');
		menuSub.set('MQTT 로그');
		menuSubSub.set('');
		fetchLogs();
	});
</script>

<div class="container-xxl flex-grow-1 container-p-y">
	<!-- 이벤트 통계 카드 -->
	<div class="row mb-4">
		<div class="col-md-3">
			<div class="card">
				<div class="card-body d-flex align-items-center py-3">
					<div class="rounded-circle p-2 me-3" style="background-color: rgba(40,167,69,0.15);">
						<i class="bx bx-log-in-circle" style="font-size: 1.5rem; color: #28a745;"></i>
					</div>
					<div>
						<div class="text-muted small">연결 (connect)</div>
						<div class="fw-bold fs-5">{getStatCount('connect').toLocaleString()}</div>
					</div>
				</div>
			</div>
		</div>
		<div class="col-md-3">
			<div class="card">
				<div class="card-body d-flex align-items-center py-3">
					<div class="rounded-circle p-2 me-3" style="background-color: rgba(220,53,69,0.15);">
						<i class="bx bx-log-out-circle" style="font-size: 1.5rem; color: #dc3545;"></i>
					</div>
					<div>
						<div class="text-muted small">끊김 (disconnect)</div>
						<div class="fw-bold fs-5">{getStatCount('disconnect').toLocaleString()}</div>
					</div>
				</div>
			</div>
		</div>
		<div class="col-md-3">
			<div class="card">
				<div class="card-body d-flex align-items-center py-3">
					<div class="rounded-circle p-2 me-3" style="background-color: rgba(0,123,255,0.15);">
						<i class="bx bx-send" style="font-size: 1.5rem; color: #007bff;"></i>
					</div>
					<div>
						<div class="text-muted small">발행 (publish)</div>
						<div class="fw-bold fs-5">{getStatCount('publish').toLocaleString()}</div>
					</div>
				</div>
			</div>
		</div>
		<div class="col-md-3">
			<div class="card">
				<div class="card-body d-flex align-items-center py-3">
					<div class="rounded-circle p-2 me-3" style="background-color: rgba(255,193,7,0.15);">
						<i class="bx bx-error" style="font-size: 1.5rem; color: #ffc107;"></i>
					</div>
					<div>
						<div class="text-muted small">유언 (will_sent)</div>
						<div class="fw-bold fs-5">{getStatCount('will_sent').toLocaleString()}</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- 필터 영역 -->
	<div class="card mb-4">
		<div class="card-header bg-light border-bottom" style="padding: 1.25rem;">
			<div class="row g-3">
				<div class="col-12">
					<div class="row align-items-end g-2">
						<div class="col-auto">
							<label for="eventTypeSelect" class="form-label mb-1" style="font-size: 12px; color: #6c757d;">이벤트 타입</label>
							<select id="eventTypeSelect" class="form-select form-select-sm" style="width: 140px;"
								bind:value={eventType} on:change={handleSearch}>
								<option value="">전체</option>
								<option value="connect">연결</option>
								<option value="disconnect">끊김</option>
								<option value="publish">발행</option>
								<option value="will_sent">유언</option>
							</select>
						</div>
						<div class="col-auto">
							<label for="mqttIdSelect" class="form-label mb-1" style="font-size: 12px; color: #6c757d;">MQTT ID</label>
							<select id="mqttIdSelect" class="form-select form-select-sm" style="width: 200px;"
								bind:value={mqttId} on:change={handleSearch}>
								<option value="">전체</option>
								{#each mqttIds as id}
									<option value={id}>{id}</option>
								{/each}
							</select>
						</div>
						<div class="col-auto">
							<label for="clientIdInput" class="form-label mb-1" style="font-size: 12px; color: #6c757d;">Client ID</label>
							<input id="clientIdInput" type="text" class="form-control form-control-sm"
								placeholder="예: DVES_ABCDEF" bind:value={clientId}
								on:keypress={handleKeyPress} style="width: 150px;" />
						</div>
						<div class="col-auto">
							<label for="topicInput" class="form-label mb-1" style="font-size: 12px; color: #6c757d;">토픽</label>
							<input id="topicInput" type="text" class="form-control form-control-sm"
								placeholder="예: stat/tasmota" bind:value={topic}
								on:keypress={handleKeyPress} style="width: 160px;" />
						</div>
						<div class="col-auto">
							<label for="startTimeInput" class="form-label mb-1" style="font-size: 12px; color: #6c757d;">시작 시간</label>
							<input id="startTimeInput" type="datetime-local" class="form-control form-control-sm"
								bind:value={startTime} style="width: 170px;" />
						</div>
						<div class="col-auto">
							<label for="endTimeInput" class="form-label mb-1" style="font-size: 12px; color: #6c757d;">종료 시간</label>
							<input id="endTimeInput" type="datetime-local" class="form-control form-control-sm"
								bind:value={endTime} style="width: 170px;" />
						</div>
						<div class="col-auto">
							<div class="d-flex gap-2">
								<button class="btn btn-primary btn-sm px-3" on:click={handleSearch} disabled={loading}>
									<i class="bx bx-search me-1"></i>검색
								</button>
								<button class="btn btn-outline-secondary btn-sm px-3" on:click={handleReset}>
									<i class="bx bx-refresh me-1"></i>초기화
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- 테이블 -->
	<div class="card">
		<div class="card-header d-flex justify-content-between align-items-center">
			<h5 class="mb-0">총 {total.toLocaleString()}건</h5>
			<TableToolbar totalItems={total} {currentPage} {totalPages} {pageSize} pageSizes={PAGE_SIZES} onPageSizeChange={handlePageSizeChange} />
		</div>
		<div class="table-responsive text-nowrap">
			<table class="table table-hover table-sm">
				<thead class="table-light">
					<tr>
						<th>No</th>
						<th>이벤트</th>
						<th>MQTT ID</th>
						<th>Client ID</th>
						<th>토픽</th>
						<th>페이로드</th>
						<th>원격 주소</th>
						<th>시간</th>
					</tr>
				</thead>
				<tbody>
					{#if loading}
						<tr>
							<td colspan="8" class="text-center py-4">
								<div class="spinner-border spinner-border-sm me-2" role="status"></div>
								<span class="ms-2">로딩중...</span>
							</td>
						</tr>
					{:else if list.length === 0}
						<tr>
							<td colspan="8" class="text-center py-4 text-muted">데이터가 없습니다</td>
						</tr>
					{:else}
						{#each list as item, index}
							<tr
								style="cursor: pointer;"
								on:click={() => toggleDetailModal(item)}
							>
								<td>{total - ((currentPage - 1) * pageSize + index)}</td>
								<td>
									<span class="badge" style="background-color: {EVENT_TYPE_COLOR[item.eventType] || '#6c757d'};">
										{EVENT_TYPE_LABEL[item.eventType] || item.eventType}
									</span>
								</td>
								<td><code class="small">{item.mqttId || '-'}</code></td>
								<td><code class="small">{truncate(item.clientId, 20)}</code></td>
								<td class="text-start"><code class="small">{truncate(item.topic, 40)}</code></td>
								<td class="text-start"><code class="small">{truncate(item.payload, 50)}</code></td>
								<td><code class="small">{item.remoteAddr || '-'}</code></td>
								<td class="small">{formatDateTime(item.createdAt)}</td>
							</tr>
						{/each}
					{/if}
				</tbody>
			</table>
		</div>
		<Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
	</div>
</div>

<!-- 상세 모달 -->
{#if showDetailModal && selectedItem}
	<div class="modal-overlay">
		<div class="modal fade show" style="display: block;" tabindex="-1" aria-modal="true" role="dialog">
			<div class="modal-dialog modal-dialog-centered modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">
							MQTT 로그 상세 -
							<span style="color: {EVENT_TYPE_COLOR[selectedItem.eventType] || '#6c757d'}">
								{EVENT_TYPE_LABEL[selectedItem.eventType] || selectedItem.eventType}
							</span>
						</h5>
						<button type="button" class="btn-close" on:click={() => toggleDetailModal(null)} />
					</div>
					<div class="modal-body">
						<div class="row mb-2">
							<div class="col-3 fw-bold">이벤트 타입</div>
							<div class="col-9">
								<span class="badge" style="background-color: {EVENT_TYPE_COLOR[selectedItem.eventType] || '#6c757d'}; color: white;">
									{EVENT_TYPE_LABEL[selectedItem.eventType] || selectedItem.eventType}
								</span>
							</div>
						</div>
						<div class="row mb-2">
							<div class="col-3 fw-bold">MQTT ID</div>
							<div class="col-9 log-text">{selectedItem.mqttId || '-'}</div>
						</div>
						<div class="row mb-2">
							<div class="col-3 fw-bold">Client ID</div>
							<div class="col-9 log-text">{selectedItem.clientId || '-'}</div>
						</div>
						<div class="row mb-2">
							<div class="col-3 fw-bold">원격 주소</div>
							<div class="col-9 log-text">{selectedItem.remoteAddr || '-'}</div>
						</div>
						<div class="row mb-2">
							<div class="col-3 fw-bold">토픽</div>
							<div class="col-9 log-text long-text">{selectedItem.topic || '-'}</div>
						</div>
						<div class="row mb-2">
							<div class="col-3 fw-bold">페이로드</div>
							<div class="col-9">
								<pre class="log-text long-text mb-0" style="max-height: 300px; overflow-y: auto;">{@html formatPayload(selectedItem.payload)}</pre>
							</div>
						</div>
						{#if selectedItem.errorMsg}
							<div class="row mb-2">
								<div class="col-3 fw-bold">에러 메시지</div>
								<div class="col-9 log-text long-text">{selectedItem.errorMsg}</div>
							</div>
						{/if}
						<div class="row mb-2">
							<div class="col-3 fw-bold">발생 시간</div>
							<div class="col-9 log-text">{formatDateTime(selectedItem.createdAt)}</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="modal-backdrop fade show" />
	</div>
{/if}

<style>
	.log-text {
		font-family: 'Consolas', monospace;
		font-size: 12px;
		background-color: #f1f5f9;
		padding: 4px 8px;
		border-radius: 4px;
	}
	.long-text {
		word-wrap: break-word;
		overflow-wrap: break-word;
		word-break: break-all;
		white-space: pre-wrap;
	}
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: 1050;
	}
	pre :global(.json-key) {
		color: #881391;
	}
	pre :global(.json-string) {
		color: #0b7285;
	}
	pre :global(.json-number) {
		color: #d9480f;
	}
	pre :global(.json-bool) {
		color: #5c7cfa;
	}
	pre :global(.json-null) {
		color: #868e96;
	}
</style>
